/**
 * Repartir las sesiones de un tratamiento en la agenda del profesional.
 *
 * El calendario y la jornada del día son el mismo componente que usan las
 * citas (`SelectorAgenda`): dar una hora se hace igual en todo el sistema.
 * Lo propio de esta pantalla es el resto — cuántas sesiones faltan, las que ya
 * tienen hora con su "Quitar", y la salida corta hacia admisión.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, Send, Trash2 } from "lucide-react";
import type { SlotLibre } from "../../api/agenda";
import { type Plan, agendarSesion, getPlan, quitarSesion } from "../../api/planes";
import { formatApiError } from "../../api/client";
import { useZonaHoraria } from "../../auth/TenantBrandingProvider";
import { Alert, Badge, Button, ConfirmDialog, Dialog } from "../ui";
import { SelectorAgenda, hoyEnZona } from "./SelectorAgenda";

export function AgendarSesionesDialog({
  plan: inicial,
  onClose,
}: {
  plan: Plan;
  onClose: () => void;
}) {
  const tz = useZonaHoraria();
  const queryClient = useQueryClient();
  // El error se muestra DENTRO del diálogo: quien está eligiendo una hora está
  // mirando aquí, y mandarlo a la tarjeta de atrás sería avisarle donde no mira.
  const [error, setError] = useState<string | null>(null);
  // Quitar aquí cancela una cita real, igual que desde la ficha: se confirma.
  const [porQuitar, setPorQuitar] = useState<number | null>(null);

  // El plan se relee tras cada sesión: sin esto el contador seguiría diciendo
  // "faltan 3" después de haber puesto la primera, y se agendarían de más.
  const { data: plan = inicial } = useQuery({
    queryKey: ["planes", "uno", inicial.id],
    queryFn: () => getPlan(inicial.id),
    initialData: inicial,
  });

  // Arranca en la fecha que propone la periodicidad. Si ese día el profesional
  // no atiende, el propio selector salta al primero que sí.
  const [dia, setDia] = useState<string>(
    () => plan.fechas_propuestas[0] ?? hoyEnZona(tz),
  );

  const faltan = plan.sesiones_totales - plan.sesiones_agendadas;

  const refrescar = () => {
    queryClient.invalidateQueries({ queryKey: ["planes"] });
    queryClient.invalidateQueries({ queryKey: ["citas"] });
    queryClient.invalidateQueries({ queryKey: ["agenda", "slots"] });
    queryClient.invalidateQueries({ queryKey: ["mi-espacio"] });
    queryClient.invalidateQueries({ queryKey: ["planes", "uno", inicial.id] });
  };

  const agendar = useMutation({
    mutationFn: (s: SlotLibre) =>
      agendarSesion(plan.id, {
        inicio: s.inicio,
        duracion_min: s.duracion_min || 30,
      }),
    onSuccess: () => {
      setError(null);
      refrescar();
      // Nunca se cierra solo, ni siquiera con la última. Poner la tercera hora
      // y verse de golpe en otra pantalla no deja revisar lo que quedó, que es
      // justo el momento en que uno quiere mirarlo entero.
    },
    onError: (e) => setError(formatApiError(e, "No se pudo agendar la sesión.")),
  });

  const quitar = useMutation({
    mutationFn: (citaId: number) => quitarSesion(plan.id, citaId),
    onSuccess: () => {
      setError(null);
      setPorQuitar(null);
      refrescar();
    },
    onError: (e) => setError(formatApiError(e, "No se pudo quitar la sesión.")),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={`Agendar sesiones · ${plan.paciente_nombre}`}
      description={`${plan.especialidad_nombre} · ${plan.profesional_nombre ?? "—"}`}
    >
      <div className="space-y-4">
        {error && <Alert>{error}</Alert>}

        {/* Cuántas van y cuántas quedan, siempre a la vista: es lo que dice si
            hay que seguir poniendo horas o ya está. */}
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-slate-600">
            {plan.sesiones_agendadas} de {plan.sesiones_totales} con hora
          </span>
          {faltan > 0 ? (
            <Badge variant="warning">
              {faltan} {faltan === 1 ? "pendiente" : "pendientes"}
            </Badge>
          ) : (
            <Badge variant="success">Todas agendadas</Badge>
          )}
          {agendar.isPending && (
            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          )}
        </div>

        <SelectorAgenda
          profesionalId={plan.profesional_id}
          sedeId={plan.sede_id}
          dia={dia}
          onDiaChange={setDia}
          onElegirSlot={(s) => {
            if (faltan > 0) agendar.mutate(s);
          }}
          planId={plan.id}
        />

        {/* Las sesiones ya puestas, para no perder la cuenta al encadenar. */}
        {plan.sesiones.length > 0 && (
          <div className="rounded-lg border border-slate-100 px-3 py-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Ya con hora
            </p>
            <ul className="mt-1 space-y-0.5">
              {plan.sesiones.map((c) => (
                <li key={c.id} className="flex items-center gap-2 text-sm text-slate-700">
                  <Check className="h-3.5 w-3.5 text-emerald-600" />
                  <span className="font-mono text-xs">{c.numero_sesion ?? "—"}</span>
                  <span>
                    {new Date(c.inicio).toLocaleString("es-PE", {
                      timeZone: tz,
                      weekday: "short",
                      day: "2-digit",
                      month: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                  {/* Deshacer aquí mismo: es donde se acaba de cometer el
                      error, y mandarlo a otra pantalla a corregirlo sería
                      hacerle perder el hilo de lo que está repartiendo. */}
                  <button
                    type="button"
                    onClick={() => setPorQuitar(c.id)}
                    disabled={quitar.isPending}
                    className="ml-auto inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium text-slate-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-40"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Quitar
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
          {faltan > 0 ? (
            <>
              {/* La salida corta: el consultorio tiene cola y ponerle hora a
                  tres sesiones no es trabajo del médico si no quiere. */}
              <Button variant="secondary" onClick={onClose}>
                <Send className="h-4 w-4" />
                Que admisión agende{" "}
                {faltan === 1 ? "la restante" : `las ${faltan} restantes`}
              </Button>
              <Button variant="ghost" onClick={onClose}>
                Cerrar
              </Button>
            </>
          ) : (
            // Con todas puestas, la acción principal es cerrar el asunto: se
            // revisa el calendario de arriba y se confirma cuando convence.
            <>
              <p className="text-sm text-emerald-700">
                Las {plan.sesiones_totales} sesiones tienen hora. Revísalas
                arriba y confirma.
              </p>
              <Button onClick={onClose}>
                <Check className="h-4 w-4" />
                Confirmar calendario
              </Button>
            </>
          )}
        </div>

        {faltan > 0 && (
          <p className="text-xs text-slate-500">
            Lo que dejes sin hora queda en la bandeja de admisión como pendiente
            de agendar. No se pierde.
          </p>
        )}

        <ConfirmDialog
          open={porQuitar !== null}
          onClose={() => setPorQuitar(null)}
          onConfirm={() => porQuitar != null && quitar.mutate(porQuitar)}
          loading={quitar.isPending}
          title="Quitar sesión"
          description="Se cancela esa cita y la sesión vuelve a quedar sin hora, lista para reprogramarse."
          confirmLabel="Sí, quitarla"
          cancelLabel="No, dejarla"
        />
      </div>
    </Dialog>
  );
}
